/**
 * The content-fallback policy (D-03).
 *
 * One object answers "which locales supply this store, in what order", and one
 * helper reports every cross-locale win. The table below is a **transcription**
 * of the orders the storefront resolved before this module existed — the chains
 * disagree by design (Spanish and Turkish category copy hops through English,
 * the repo FAQ drops straight to German, email serves English today), so
 * normalising them into one idealised order would silently change what visitors
 * read (Pitfall 5). Unifying the orders is Phase 4/5 work; it requires the copy
 * to exist first.
 *
 * The flag is additive and observable, never a rendering change: a win produced
 * by a different locale than the one requested increments a process-scoped
 * counter and emits one `[fallback]` warning carrying identifiers only —
 * `store`, `requested`, `winner` — and never a resolved content value
 * (T-02-10/11/12). Nothing here is conditional on a build-time flag.
 *
 * Source anchors for every row, read from the pre-change tree:
 * `src/config/locale.ts` (`contentFallbackChain`), `src/lib/category/content/index.ts:4-9,17`,
 * `src/config/peptide-faq.ts:35`, `src/lib/email/email-base.ts:47-54`,
 * `src/config/brand.ts:113-120`, `src/lib/product-ux.ts:250-256`,
 * `src/lib/affiliate/program-rollout-policy.ts:12`, `src/lib/campaigns/labels.ts`.
 */

import { getLocaleConfig, type Locale } from "@/config/locale";

export type ContentStore =
	| "messages"
	| "categoryContent"
	| "peptideFaq"
	| "marketCopy"
	| "saleorEntity"
	| "emailCopy"
	| "adminCopy"
	| "brandCopy";

/** Every store the policy declares, in the order this file reads them. */
export const contentStores: readonly ContentStore[] = [
	"messages",
	"categoryContent",
	"peptideFaq",
	"marketCopy",
	"saleorEntity",
	"emailCopy",
	"adminCopy",
	"brandCopy",
];

/**
 * Category copy first tries the requested locale. English and German remain
 * fallbacks for category modules that have not yet been authored in that locale.
 * The weight-management category has authored copy in all twelve public locales.
 */
const CATEGORY_CHAINS: Partial<Record<Locale, readonly Locale[]>> = {
	de: ["de", "en"],
	en: ["en", "de"],
	es: ["es", "en", "de"],
	tr: ["tr", "en", "de"],
};

/** The chain a category locale without an authored module falls through. */
const UNAUTHORED_CATEGORY_CHAIN: readonly Locale[] = ["en", "de"];

/**
 * `src/lib/email/email-base.ts:47-54` (`resolveEmailLocale`): an unsupported
 * locale resolves to `"en"`. Declared, not migrated — widening `EmailLocale` is
 * Phase 6, and its label maps are compile-forced `Record<EmailLocale, …>`.
 */
const EMAIL_REFERENCE_LOCALE: Locale = "en";

/**
 * The language `brandConfig.tagline` / `brandConfig.description` are authored
 * in. `brandCopy`'s second hop, for the same reason as `emailCopy`'s.
 */
const BRAND_REFERENCE_LOCALE: Locale = "en";

/**
 * The per-store orders, transcribed.
 *
 * The four registry-chain stores read the registry's own `contentFallbackChain`
 * rather than re-declaring `[locale, "de"]`, so the registry stays the single
 * source of truth for the reference language and the default locale's
 * single-element chain (`["de"]`).
 */
const CHAIN_BUILDERS: Record<ContentStore, (locale: Locale) => readonly Locale[]> = {
	messages: (locale) => getLocaleConfig(locale).contentFallbackChain,
	peptideFaq: (locale) => getLocaleConfig(locale).contentFallbackChain,
	saleorEntity: (locale) => getLocaleConfig(locale).contentFallbackChain,
	adminCopy: (locale) => getLocaleConfig(locale).contentFallbackChain,
	marketCopy: (locale) => [locale],
	emailCopy: (locale) => [locale, EMAIL_REFERENCE_LOCALE],
	brandCopy: (locale) => [locale, BRAND_REFERENCE_LOCALE],
	categoryContent: (locale) => CATEGORY_CHAINS[locale] ?? [locale, ...UNAUTHORED_CATEGORY_CHAIN],
};

/**
 * The ordered locales that supply `store` for `locale`.
 *
 * Never empty and never throwing: every chain terminates at an authored locale,
 * and the registry chain collapses to a single element for the default locale.
 */
export function getContentChain(locale: Locale, store: ContentStore): readonly Locale[] {
	return CHAIN_BUILDERS[store](locale);
}

// ─── The observable flag ─────────────────────────────────────────────────────

/**
 * Process-scoped fallback counter, keyed by `store:requested->winner`.
 *
 * Deliberately module-scope and not per-request state: it measures a defect
 * rate over a server process's lifetime, and the bounded cross-product of eight
 * stores, twelve locales and at most three hops cannot grow it without bound
 * (T-02-13). It is read by tests today; a reporting surface is Phase 10's.
 */
const fallbackCounts = new Map<string, number>();

export type ContentLookup<T> = (locale: Locale) => T | null | undefined;

export type AsyncContentLookup<T> = (locale: Locale) => Promise<T | null | undefined>;

export interface ResolvedContent<T> {
	value: T;
	locale: Locale;
}

/**
 * A win is cross-locale when the winning locale is not the locale that was
 * asked for.
 *
 * `requested` defaults to the chain's first element, which is the requested
 * locale for every store except `categoryContent` without authored copy — that
 * store's transcribed chain begins at `en` (the locale it hops through), so a
 * chain-walking caller must pass the requested locale explicitly. A same-locale
 * win is never flagged, and neither is an undefined winner.
 */
export function isCrossLocaleWin(
	chain: readonly Locale[],
	winner: Locale | undefined,
	requested: Locale = chain[0],
): boolean {
	if (winner === undefined) return false;
	return winner !== requested;
}

/**
 * Record one cross-locale win: one counter increment and one warning carrying
 * the three identifiers. Never pass a resolved value here (T-02-10).
 */
export function noteFallback(store: ContentStore, requested: Locale, winner: Locale): void {
	const key = `${store}:${requested}->${winner}`;

	fallbackCounts.set(key, (fallbackCounts.get(key) ?? 0) + 1);
	console.warn(`[fallback] ${store} ${requested} served ${winner}`);
}

/**
 * The process-scoped report, read by tests and (from Phase 10) by a reporting
 * surface. Every entry carries identifiers and a count — never a resolved value.
 */
export function getFallbackReport(): ReadonlyArray<{
	store: string;
	requested: string;
	winner: string;
	count: number;
}> {
	return [...fallbackCounts].map(([key, count]) => {
		const [store, hop] = key.split(":");
		const [requested, winner] = hop.split("->");

		return { store, requested, winner, count };
	});
}

/** Clear the counter. A test seam; no rendering path calls it. */
export function resetFallbackReport(): void {
	fallbackCounts.clear();
}

/**
 * Walk `store`'s chain for `requested` and return the first non-nullish lookup
 * result together with the locale that supplied it.
 *
 * Additive by contract: `null` means the chain was exhausted, and the caller's
 * own terminal behaviour (the category store's throw, the FAQ store's `null`)
 * is unchanged. Every cross-locale win is reported through `noteFallback`.
 */
export function resolveContent<T>(
	store: ContentStore,
	requested: Locale,
	lookup: ContentLookup<T>,
): ResolvedContent<T> | null {
	const chain = getContentChain(requested, store);

	for (const locale of chain) {
		const value = lookup(locale);

		if (value === null || value === undefined) continue;

		if (isCrossLocaleWin(chain, locale, requested)) {
			noteFallback(store, requested, locale);
		}

		return { value, locale };
	}

	return null;
}

/** `resolveContent` for a lookup that loads asynchronously (a dynamic import, a fetch). */
export async function resolveContentAsync<T>(
	store: ContentStore,
	requested: Locale,
	lookup: AsyncContentLookup<T>,
): Promise<ResolvedContent<T> | null> {
	const chain = getContentChain(requested, store);

	for (const locale of chain) {
		const value = await lookup(locale);

		if (value === null || value === undefined) continue;

		if (isCrossLocaleWin(chain, locale, requested)) {
			noteFallback(store, requested, locale);
		}

		return { value, locale };
	}

	return null;
}
